<template>
  <div class="medicines-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="medicines-container">
      <!-- Header Section -->
      <div class="medicines-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-capsules title-icon"></i>
              Medicamentos
            </h1>
            <p class="page-subtitle">
              Gerencie o catálogo de medicamentos da instituição
            </p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'medicine')"
              class="btn-add-modern"
              @click="handleModalCreate"
            >
              <i class="fas fa-plus mr-2"></i>
              Adicionar
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="medicines-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-search filter-icon"></i>
                Pesquisar medicamentos
              </label>
              <v-text-field
                v-model="search"
                placeholder="Digite o nome ou princípio ativo"
                outlined
                dense
                hide-details
                class="modern-input"
                @input="paginate"
              />
            </div>
          </div>
        </div>

        <!-- Data Table Section -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <v-data-table
                :headers="headers"
                :items="medicines"
                item-key="`${type}`/${id}"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                :options.sync="options"
                :server-items-length="total"
                class="modern-table"
                :no-data-text="'Nenhum medicamento encontrado'"
                :loading-text="'Carregando medicamentos...'"
              >
                <template v-slot:[`item.name`]="{ item }">
                  <div class="medicine-name-cell">
                    <v-tooltip bottom max-width="600">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="medicine-name">
                          {{ item.name }}
                        </div>
                      </template>
                      <span>{{ item.name }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template v-slot:[`item.active_ingredient`]="{ item }">
                  <div class="ingredient-cell">
                    <v-tooltip bottom max-width="600">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="ingredient-name">
                          {{ item.active_ingredient }}
                        </div>
                      </template>
                      <span>{{ item.active_ingredient }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                  <span
                    class="type-badge"
                    :class="
                      item.type === 'PADRAO' ? 'type-standard' : 'type-company'
                    "
                  >
                    {{ item.type === "PADRAO" ? "PADRÃO" : "EMPRESA" }}
                  </span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn
                    class="btn-action-edit"
                    small
                    @click="handleToMedicine(item)"
                  >
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>
                </template>
              </v-data-table>
            </b-overlay>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Create -->
    <b-modal
      no-close-on-backdrop
      size="xl"
      id="createMedicine"
      hide-footer
      modal-class="modern-modal"
    >
      <template v-slot:modal-title>
        <div class="modal-title-modern">
          <i class="fas fa-capsules mr-2"></i>
          Novo Medicamento
        </div>
      </template>

      <div class="modal-content-modern">
        <form @submit="create" method="post">
          <div class="form-grid">
            <!-- Nome Comercial -->
            <div class="form-group full-width">
              <LabelRequiredField label="Nome Comercial" />
              <input
                type="text"
                ref="autofocus"
                v-model="name"
                class="modern-form-input"
                placeholder="Digite o nome comercial do medicamento"
              />
            </div>

            <!-- Princípio Ativo -->
            <div class="form-group full-width">
              <SelectActiveIngredientID
                v-model="active_ingredient_id"
                class="modern-select"
              />
            </div>

            <!-- Lista -->
            <div class="form-group">
              <label class="form-label">Lista</label>
              <select v-model="list" class="modern-form-select">
                <option value="">Selecione...</option>
                <option
                  v-for="value in lists"
                  :key="value.id"
                  :value="value.name"
                >
                  {{ String(value.name).toUpperCase() }}
                </option>
              </select>
            </div>

            <!-- Tipo de Receita -->
            <div class="form-group">
              <label class="form-label">Tipo de Receita</label>
              <select v-model="type_of_recipe_id" class="modern-form-select">
                <option value="">Selecione...</option>
                <option
                  v-for="value in typesOfRecipe"
                  :key="value.id"
                  :value="value.id"
                >
                  {{ String(value.name).toUpperCase() }}
                </option>
              </select>
            </div>

            <!-- Unidade de Medida -->
            <div class="form-group">
              <LabelRequiredField label="Unidade de Medida" />
              <select
                v-model="unit_of_measurement_id"
                class="modern-form-select"
              >
                <option value="">Selecione...</option>
                <option
                  v-for="value in unitOfMeasurements"
                  :key="value.id"
                  :value="value.id"
                >
                  {{ value.name }}
                </option>
              </select>
            </div>

            <!-- Classe Terapêutica -->
            <div class="form-group">
              <label class="form-label">Classe Terapêutica</label>
              <input
                type="text"
                v-model="therapeutic_class"
                class="modern-form-input"
                placeholder="Digite a classe terapêutica"
              />
            </div>

            <!-- Tipo de Medicamento -->
            <div class="form-group">
              <label class="form-label">Tipo de Medicamento</label>
              <select v-model="type_of_medicine_id" class="modern-form-select">
                <option value="">Selecione...</option>
                <option
                  v-for="value in typesOfMedicine"
                  :key="value.id"
                  :value="value.id"
                >
                  {{ String(value.name).toUpperCase() }}
                </option>
              </select>
            </div>

            <!-- Custo do Medicamento -->
            <div class="form-group">
              <label class="form-label">Custo do Medicamento</label>
              <input
                type="text"
                v-model="medicine_cost"
                class="modern-form-input"
                placeholder="R$ 0,00"
                @keyup="handleValueMedicineCost"
              />
            </div>

            <!-- Lucro -->
            <div class="form-group">
              <label class="form-label">Lucro (%)</label>
              <input
                type="text"
                v-model="profit"
                class="modern-form-input"
                placeholder="0,00%"
                @keyup="handleValueProfit"
              />
            </div>

            <!-- Valor da Venda -->
            <div class="form-group">
              <label class="form-label">Valor da Venda</label>
              <input
                type="text"
                v-model="sale_price"
                class="modern-form-input"
                placeholder="R$ 0,00"
                @keyup="handleValueSalePrice"
              />
            </div>
          </div>

          <!-- Warning and Errors -->
          <div class="form-footer">
            <WarningRequiredFields />
            <ErrorsMessages :errors="errors" />

            <div class="form-actions">
              <v-btn
                :disabled="loadingCreate"
                type="submit"
                class="btn-save-modern"
              >
                <i class="fas fa-save mr-2"></i>
                {{ loadingCreate ? "Salvando..." : "Salvar Medicamento" }}
              </v-btn>
            </div>
          </div>
        </form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import WarningRequiredFields from "../templates/Components/WarningRequiredFields";
import LabelRequiredField from "../templates/Components/LabelRequiredField";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import SelectActiveIngredientID from "../templates/Components/SelectActiveIngredientID";

import api from "../services/api";
import routes from "../routes";
import toast from "../lib/ToastNotification";
import permission from "../lib/permission";

import {
  formatPrice,
  formatValueOnKeyUp,
  formatValueToOnlyNumber,
  formatValueToPercentage,
  formatDate,
} from "../lib/format";

export default {
  name: "Medicines",
  components: {
    NavBar,
    AuthBySessionExpired,
    SelectActiveIngredientID,
    ErrorsMessages,
    WarningRequiredFields,
    LabelRequiredField,
  },
  data() {
    return {
      total: 0,
      options: {},
      search: "",
      headers: [
        { text: "NOME COMERCIAL", value: "name" },
        { text: "PRINCÍPIO ATIVO", value: "active_ingredient" },
        { text: "TIPO", value: "type" },
        { text: "AÇÕES", value: "actions", sortable: false },
      ],
      rest_home_id: "",
      lists: [
        { id: 1, name: "A1" },
        { id: 2, name: "A2" },
        { id: 3, name: "A3" },
        { id: 4, name: "B1" },
        { id: 5, name: "C1" },
        { id: 6, name: "C5" },
        { id: 7, name: "D1" },
      ],
      medicines: [],
      unitOfMeasurements: [],
      typesOfMedicine: [],
      typesOfRecipe: [],
      unit_of_measurement_id: "",
      type_of_medicine_id: "",
      type_of_recipe_id: "",
      medicine: {},
      id: "",
      name: "",
      therapeutic_class: "",
      active_ingredient: [],
      active_ingredient_id: "",
      list: "",
      medicine_cost: "",
      profit: "",
      sale_price: "",
      loading: false,
      loadingCreate: false,
      errors: [],
    };
  },
  watch: {
    options: {
      handler() {
        this.paginate();
      },
    },
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,
    formatPrice,
    formatValueOnKeyUp,
    formatValueToOnlyNumber,
    formatValueToPercentage,
    formatDate,
    handleToBack: () => routes.go(-1),
    handleToMedicine({ id }) {
      routes.push({ name: "medicine", params: { id } });
    },
    handleValueProfit: function (e) {
      var profit = formatValueToPercentage(e.target.value);
      if (profit) {
        var current = formatValueToOnlyNumber(this.medicine_cost);
        var percentage = profit * current;
        var sale_price = percentage / 100;
        sale_price = parseFloat(sale_price) + parseFloat(current);
        this.sale_price = formatPrice(sale_price);
      } else {
        this.sale_price = formatPrice(0);
      }
    },
    handleValueMedicineCost: function (e) {
      this.medicine_cost = formatValueOnKeyUp(e.target.value);

      var current = formatValueToOnlyNumber(e.target.value);
      var sale_price = formatValueToOnlyNumber(this.sale_price);

      if (parseInt(current) <= parseInt(sale_price)) {
        var percentage = parseFloat(sale_price - current);
        var profit = percentage * 100;
        profit = profit / current;
        profit = String(profit);
        this.profit = profit.replace(".", ",");
      }
    },
    handleValueSalePrice: function (e) {
      this.sale_price = formatValueOnKeyUp(e.target.value);

      var current = formatValueToOnlyNumber(this.medicine_cost);
      var sale_price = formatValueToOnlyNumber(e.target.value);

      if (parseInt(current) <= parseInt(sale_price)) {
        var percentage = parseFloat(sale_price - current);
        var profit = percentage * 100;
        profit = profit / current;
        profit = String(profit);
        this.profit = profit.replace(".", ",");
      }
    },
    async retrieve() {
      if (this.errors.length == 0) await this.getUnitOfMeasurements();
      if (this.errors.length == 0) await this.getActiveIngredient();
      if (this.errors.length == 0) await this.getTypesOfMedicine();
      if (this.errors.length == 0) await this.getTypesOfRecipe();
    },
    async paginate() {
      try {
        this.loading = true;

        const { rest_home_id, search } = this;
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        let params = {
          rest_home_id,
          page,
          itemsPerPage,
          search,
          sortBy,
          sortDesc,
        };

        const response = await api.get("/rest-home/medicine/paginate", {
          params,
        });

        const { error, errors, medicines, total } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.medicines = medicines;
          this.total = total;
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async getUnitOfMeasurements() {
      try {
        const response = await api.get("/rest-home/unit-of-measurement/all");
        const { error, errors, unit_of_measurement } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.unitOfMeasurements = unit_of_measurement;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getActiveIngredient() {
      try {
        const response = await api.get("/rest-home/active-ingredients");
        const { error, errors, active_ingredient } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.active_ingredient = active_ingredient;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getTypesOfMedicine() {
      try {
        const response = await api.get("/rest-home/type-of-medicine/all");
        const { error, errors, types } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.typesOfMedicine = types;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getTypesOfRecipe() {
      try {
        const response = await api.get("/rest-home/type-of-recipe/all");
        const { error, errors, types } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.typesOfRecipe = types;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async create(event) {
      try {
        event.preventDefault();
        this.loadingCreate = true;

        const {
          rest_home_id,
          unit_of_measurement_id,
          active_ingredient_id,
          type_of_medicine_id,
          type_of_recipe_id,
          name,
          therapeutic_class,
          list,
          medicine_cost,
          profit,
          sale_price,
        } = this;

        let params = {
          rest_home_id,
          unit_of_measurement_id,
          active_ingredient_id,
          type_of_medicine_id,
          type_of_recipe_id,
          name,
          therapeutic_class,
          list,
          medicine_cost,
          profit,
          sale_price,
        };

        const response = await api.post("/rest-home/medicine/create", params);
        const { error, errors, message } = response.data;

        if (!error) {
          this.$bvModal.hide("createMedicine");
          this.paginate();
          toast(message, "success");
        } else {
          this.errors = errors;
        }
        this.loadingCreate = false;
      } catch (error) {
        console.log(error);
      }
    },
    handleModalCreate() {
      this.unit_of_measurement_id = "";
      this.active_ingredient_id = "";
      this.type_of_medicine_id = "";
      this.type_of_recipe_id = "";
      this.name = "";
      this.therapeutic_class = "";
      this.medicine_cost = "";
      this.profit = "";
      this.sale_price = "";
      this.errors = [];
      this.$bvModal.show("createMedicine");
      setTimeout(() => this.$refs.autofocus.focus(), 10);
    },
  },
};
</script>

<style scoped>
.medicines-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.medicines-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.medicines-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Content Section */
.medicines-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

/* Table Section */
.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
}

/* Table Styling */
.modern-table {
  background: transparent !important;
}

.medicine-name-cell,
.ingredient-cell {
  max-width: 300px;
}

.medicine-name,
.ingredient-name {
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-standard {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.type-company {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* Modal Styling */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-content-modern {
  padding: 8px 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.modern-form-input,
.modern-form-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-form-input:focus,
.modern-form-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

.form-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
  margin-top: 24px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.btn-save-modern {
  background: linear-gradient(135deg, #1e884d 0%, #16a34a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(30, 136, 77, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-save-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(30, 136, 77, 0.4) !important;
}

.btn-save-modern:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .medicines-container {
    padding: 0 16px;
  }

  .medicines-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
    padding: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modal-content-modern {
    padding: 0;
  }
}

@media (max-width: 480px) {
  .btn-back-modern,
  .btn-add-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-input >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
  padding: 12px 0 !important;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.modern-modal >>> .modal-body {
  padding: 24px !important;
}
</style>